import React, {PropsWithChildren} from 'react';
import {useQuery} from '@tanstack/react-query';
import {API} from '@chekin/api';
import {
  BookingSummary,
  SUMMARY_PAYMENTS_STATUSES,
  LocalStorage,
  QUERY_CACHE_KEYS,
  getAreAllPaymentsCompleted,
  getArePaymentsAvailable,
  getIsGuestRegistrationOnly,
  resolveHousingName,
  RESERVATION_ID_KEY,
} from '@chekin/core';
import {useIsTokenReady} from '../ChekinSDK';
import {useReservation} from '../reservation';
import {PATHS} from 'routes/constants';
import {checkIsPaymentNotComplete} from './utils';

enum BASE_PATHS {
  home = 'home',
  onlineChekin = 'onlineChekin',
}

export type ContextProps = {
  housingName: string;
  countryCode: string;
  reservationId: string;
  isGuestappComplete: boolean;
  checkIfAnyPaymentNotComplete: (exemption?: 'insurance' | 'taxes') => boolean;
  data?: BookingSummary;
  error?: Error | null;
  arePaymentsAvailable?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  isLoadingError?: boolean;
  isSuccess?: boolean;
  isGuestRegistrationOnly: boolean;
  isGuestRegistrationComplete: boolean;
  basePaths: {[key in BASE_PATHS]: string};
  areAllPaymentsCompleted?: boolean;
  summaryStatus: 'error' | 'success' | 'loading' | 'pending';
  isSecurityDepositPending: boolean;
  isInstantCheckInEnabled: boolean;
};

const SummaryContext = React.createContext<ContextProps>({
  data: undefined,
  isLoading: false,
  isFetching: false,
  countryCode: '',
  reservationId: '',
  housingName: '',
  isGuestappComplete: false,
  checkIfAnyPaymentNotComplete: () => true,
  isGuestRegistrationOnly: true,
  isGuestRegistrationComplete: false,
  isInstantCheckInEnabled: false,
  basePaths: {[BASE_PATHS.home]: '', [BASE_PATHS.onlineChekin]: ''},
  summaryStatus: 'loading',
  isSecurityDepositPending: true,
});

function SummaryProvider({children}: Readonly<PropsWithChildren>) {
  const {data: reservation} = useReservation();
  const isReadyToken = useIsTokenReady();
  const reservationId =
    reservation?.id ?? LocalStorage.get<string>(RESERVATION_ID_KEY) ?? '';

  const enabled = isReadyToken && Boolean(reservationId);
  const {
    data,
    error,
    isLoading: isInternalLoading,
    isFetching,
    isLoadingError,
    isSuccess,
    status,
  } = useQuery<BookingSummary, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.summary],
    queryFn: () => API.reservation.getSummary(reservationId || ''),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  const isLoading = Boolean(reservationId && isInternalLoading);

  const summary = React.useMemo(() => data || ({} as BookingSummary), [data]);
  const isSecurityDepositPending =
    data?.property_protection.status === SUMMARY_PAYMENTS_STATUSES.configurationPending;
  const isGuestRegistrationOnly = getIsGuestRegistrationOnly(summary);

  const countryCode = React.useMemo(
    function getHousingCountryCode(): string {
      const code = data?.country_code;

      if (!code) {
        return '';
      }

      return code;
    },
    [data?.country_code],
  );

  const arePaymentsAvailable = React.useMemo(() => getArePaymentsAvailable(data), [data]);

  const housingName = resolveHousingName(summary) || '';
  const checkIfAnyPaymentNotComplete = React.useCallback(
    (exemption?: 'insurance' | 'taxes') => {
      return (
        (checkIsPaymentNotComplete(summary.property_protection) &&
          exemption !== 'insurance') ||
        (checkIsPaymentNotComplete(summary.taxes) && exemption !== 'taxes')
      );
    },
    [summary.property_protection, summary.taxes],
  );

  const basePaths = React.useMemo(() => {
    return {
      [BASE_PATHS.home]: PATHS.home,
      [BASE_PATHS.onlineChekin]: isGuestRegistrationOnly
        ? PATHS.guestRegistration
        : PATHS.home,
    };
  }, [isGuestRegistrationOnly]);

  const areAllPaymentsCompleted = React.useMemo(
    () => getAreAllPaymentsCompleted(data),
    [data],
  );
  const isGuestRegistrationComplete = data?.guest_registration?.status === 'COMPLETE';

  const isInstantCheckInEnabled = Boolean(data?.is_instant_check_in_enabled);
  const isGuestappComplete = arePaymentsAvailable
    ? areAllPaymentsCompleted && isGuestRegistrationComplete
    : isGuestRegistrationComplete;

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: summary,
      error,
      isLoading,
      isFetching,
      isLoadingError,
      countryCode,
      reservationId,
      isSuccess,
      housingName,
      isGuestRegistrationComplete,
      isGuestappComplete,
      arePaymentsAvailable,
      checkIfAnyPaymentNotComplete,
      isGuestRegistrationOnly,
      basePaths,
      areAllPaymentsCompleted,
      summaryStatus: status,
      isInstantCheckInEnabled,
      isSecurityDepositPending,
    }),
    [
      isGuestappComplete,
      checkIfAnyPaymentNotComplete,
      countryCode,
      error,
      arePaymentsAvailable,
      housingName,
      isFetching,
      isLoading,
      isLoadingError,
      isSuccess,
      reservationId,
      summary,
      isGuestRegistrationOnly,
      basePaths,
      isGuestRegistrationComplete,
      areAllPaymentsCompleted,
      status,
      isInstantCheckInEnabled,
      isSecurityDepositPending,
    ],
  );

  return <SummaryContext.Provider value={defaultProps} children={children} />;
}

export {SummaryContext, SummaryProvider};
